/**
 * Canal de publicação: a tela conta o que o DOM não consegue contar.
 *
 * O observador genérico lê o DOM e isso cobre formulário, tabela e aba. Não
 * cobre a folha de ponto: as linhas dela são `div`s sem papel de tabela e,
 * acima de 40 linhas, um scroller virtual mantém no DOM apenas a janela
 * visível. Raspar aquele DOM devolve um punhado de `input`s de hora sem dizer
 * de que dia ou de quem são — foi exatamente o "pouco contexto" relatado.
 *
 * Por isso a tela pode PUBLICAR o que mostra. Continua valendo a fronteira: o
 * que sai daqui descreve a TELA, nunca o banco — mesma classe de confiança do
 * que a pessoa digita no chat. Publicar não é autorizar nada.
 *
 * Uma tela por vez: quem monta registra, quem desmonta desregistra. O
 * observador colhe o que estiver registrado no instante do envio do turno.
 */
import { TipoDeItemDeTela } from './tiposDeItemDeTela'
import { criarLog } from '../../servicos/logDoAssistente'

const log = criarLog('contextoDaTela')

export interface ItemPublicadoPelaTela {
  /** Rótulo como a pessoa o vê. */
  rotulo: string | null
  /** Valor exibido. Vazio é vazio: a ausência também é informação. */
  valor: string
  /** Tipo do vocabulário fechado; decide em qual das três listas o item cai. */
  tipo: TipoDeItemDeTela
  /** Identificador estável do item, quando a tela tiver um. */
  nome?: string | null
  /** Só para ações: a tela mostra o controle, mas ele não está acionável. */
  desabilitado?: true
}

export interface ContextoPublicadoPelaTela {
  /**
   * Identificador da tela, no vocabulário do produto ("folha-de-ponto").
   * Nunca a URL: é este nome que o modelo repete para a pessoa.
   */
  tela: string
  /** Título legível, como aparece para a pessoa. */
  titulo?: string | null
  estrutura?: ItemPublicadoPelaTela[]
  dados?: ItemPublicadoPelaTela[]
  acoes?: ItemPublicadoPelaTela[]
}

export type ProvedorDeContextoDaTela = () => ContextoPublicadoPelaTela | null

let provedor: ProvedorDeContextoDaTela | null = null

export function registrarProvedorDeContextoDaTela (novo: ProvedorDeContextoDaTela | null): void {
  log.debug(novo === null ? 'provedor de contexto removido' : 'provedor de contexto registrado')
  provedor = novo
}

/**
 * O que a tela publicou AGORA, ou `null`.
 *
 * Provedor que estoura degrada para `null`: uma tela com defeito na descrição
 * de si mesma não pode derrubar o turno da pessoa — sem publicação, o
 * observador segue com o que o DOM der.
 */
export function colherContextoPublicadoPelaTela (): ContextoPublicadoPelaTela | null {
  if (provedor === null) return null
  try {
    const publicado = provedor()
    if (!publicado || typeof publicado.tela !== 'string' || publicado.tela.length === 0) {
      log.debug('a tela não publicou contexto', publicado)
      return null
    }
    log.debug(`contexto colhido da tela ${publicado.tela}`, {
      estrutura: publicado.estrutura?.length ?? 0,
      dados: publicado.dados?.length ?? 0,
      acoes: publicado.acoes?.length ?? 0
    })
    return publicado
  } catch (erro) {
    log.error('o provedor de contexto da tela quebrou', erro)
    return null
  }
}
